import { useEffect, useState, useContext } from 'react';
import './HomePage.css';
import { Edit, Eye } from 'lucide-react';
import { HomepageCacheContext } from './context/HomepageCacheContext';
import HeroSection from './components/HeroSection';
import QuickLinksSection from './components/QuickLinksSection';
import HighlightsSection from './components/HighlightsSection';
import NewsCountdownSection from './components/NewsCountdownSection';
import {
  HeroSkeleton,
  QuickLinksSkeleton,
  HighlightsSkeleton,
  NewsCountdownSkeleton,
  FooterSkeleton
} from './components/SkeletonLoaders';
import { homepageContent } from './data/homepageContent';
import { calculateCountdown, getOpeningDayTimestamp } from './utils/countdownCalculator';
import { getInitialHighlightIndex, getNextHighlightIndex, validateHighlightIndex, HIGHLIGHT_ROTATION_INTERVAL } from './utils/highlightRotation';
import newsImage1 from './assets/news/news1.jpg';
import newsImage2 from './assets/news/new2.jpg';
import newsImage3 from './assets/news/news3.jpg';
import newsImage4 from './assets/news/news4.jpg';
import newsImage5 from './assets/news/news5.jpg';
import newsImage6 from './assets/news/news6.jpg';
import newsImage7 from './assets/news/news7.jpg';
import newsImage8 from './assets/news/news8.jpg';

const translations = homepageContent;

const highlightPhotos = [
  newsImage1,
  newsImage2,
  newsImage3,
  newsImage4,
  newsImage5,
  newsImage6,
  newsImage7,
  newsImage8,
];

const API_BASE = (() => {
  // Try env variable first
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on current domain
  const protocol = window.location.protocol; // http: or https:
  const hostname = window.location.hostname; // localhost, example.com, etc
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Development - point to PRODUCTION API at /api
    return 'https://permataharapansecondary.sch.id/api';
  }
  
  // Production - API on same domain under /api
  return `${protocol}//${hostname}/api`;
})();

console.log('[HomePage] API_BASE configured as:', API_BASE);

function parseJsonContent(value) {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    return value;
  }

  return null;
}

function formatCountdownDate(dateValue, language) {
  if (!dateValue) {
    return '';
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatNewsDate(dateValue, language) {
  if (!dateValue) {
    return '';
  }

  // Handle format "2026-07-20" or "July 20, 2026"
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue; // Return original if can't parse
  }

  // Format: "Jul 20, 2026" (month shorthand + day + year)
  const monthShort = parsedDate.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
    month: 'short'
  });
  const day = parsedDate.getDate();
  const year = parsedDate.getFullYear();
  
  if (language === 'id') {
    return `${day} ${monthShort} ${year}`;
  }
  return `${monthShort} ${day}, ${year}`;
}

function resolveMediaUrl(mediaId, directUrl, mediaMap = {}) {
  let url = null;

  // PRIORITY 1: mediaMap lookup if mediaId exists (true URL from database)
  if (mediaId && mediaMap) {
    if (mediaMap[mediaId]) url = mediaMap[mediaId];
    else if (mediaMap[String(mediaId)]) url = mediaMap[String(mediaId)];
  }
  
  // PRIORITY 2: Direct URL, but REJECT fake backend fallback patterns like /uploads/homepage/highlights/media_\d+\.jpg
  if (!url && directUrl && typeof directUrl === 'string') {
    if (/\/uploads\/homepage\/(highlights|media)\/media_[^/]+\.jpg$/i.test(directUrl)) {
      // Fake synthesized URL that causes 404, reject it
    } else {
      url = directUrl;
    }
  }
  
  // PRIORITY 3: If mediaId is itself a full URL or path
  if (!url && mediaId) {
    const str = String(mediaId);
    if (str.startsWith('http://') || str.startsWith('https://')) url = str;
    else if (str.startsWith('/')) url = str;
    else if (str.startsWith('media_')) url = `/api/uploads/homepage/highlights/${str}`;
    else if (str.includes('.')) url = `/api/uploads/images/${str}`;
  }
  
  if (url && typeof url === 'string') {
    if (url.startsWith('/uploads/') && !url.startsWith('/api/uploads/')) {
      url = `/api${url}`;
    }
    // CRITICAL FIX: Files starting with highlight_ are physically stored in /api/uploads/images/, NOT in /uploads/homepage/highlights/
    if (url.includes('highlight_') && url.includes('/uploads/homepage/')) {
      const filename = url.split('/').pop().split('?')[0];
      const hostMatch = url.match(/^(https?:\/\/[^/]+)/i);
      const origin = hostMatch ? hostMatch[1] : '';
      url = `${origin}/api/uploads/images/${filename}`;
    }
    // Remove duplicate /api/api if present
    url = url.replace(/\/api\/api\//g, '/api/');
    return url;
  }
  
  return null;
}

function HomePage({ 
  language = 'en', 
  onLanguageChange, 
  onNavigate,
  editableMode = false,
  selectedSection = null,
  sections = null,
  onOpenItemManager = null,
  onEditSection = null
}) {
  const cacheContext = useContext(HomepageCacheContext);
  const [localLanguage, setLocalLanguage] = useState(language);
  const [cmsData, setCmsData] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_sections_homepage');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {}
    return [];
  });
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') return parsed;
      }
    } catch (e) {}
    return {};
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // DEBUG: Log state changes
  useEffect(() => {
    console.log('[HomePage.js] STATE CHANGE: cmsData updated');
    console.log('[HomePage.js]   cmsData type:', typeof cmsData);
    console.log('[HomePage.js]   cmsData is array:', Array.isArray(cmsData));
    console.log('[HomePage.js]   cmsData length:', cmsData?.length);
    if (cmsData && Array.isArray(cmsData)) {
      const h = cmsData.find(s => s.section_type === 'hero');
      console.log('[HomePage.js]   hero section in cmsData:', !!h);
      if (h) {
        console.log('[HomePage.js]   hero video_url:', h.content?.en?.video_url);
      }
      // Save to localStorage for EMERGENCY fallback only
      try {
        localStorage.setItem('cms_sections_cache', JSON.stringify(cmsData));
        console.log('[HomePage.js] ✓ Saved cmsData to localStorage (emergency fallback)');
      } catch (e) {
        console.warn('[HomePage.js] Could not save to localStorage:', e);
      }
    }
  }, [cmsData]);
  
  const activeLanguage = onLanguageChange ? language : localLanguage;
  const setLanguage = onLanguageChange || setLocalLanguage;
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [externalNewsArticles, setExternalNewsArticles] = useState(null);
  
  console.log('[HomePage.js] activeLanguage:', activeLanguage);
  
  // (Hero fetch removed - using main fetch only to avoid duplication)

  // ================================================================================
  // SMART FETCH: Check cache first, fetch if stale, timeout 5s max
  // Shows cached data INSTANTLY, then updates in background
  // ================================================================================
  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        // STEP 1: Check if we have fresh cached data (< 60 seconds old)
        const cachedData = sessionStorage.getItem('cms_sections_homepage');
        const cachedMedia = sessionStorage.getItem('cms_media_homepage');
        const cacheTime = sessionStorage.getItem('cms_cache_time');
        const now = Date.now();
        const cacheAgeMs = cacheTime ? (now - parseInt(cacheTime)) : Infinity;
        const CACHE_TTL_MS = 60000; // 60 seconds
        
        let hasFreshCache = false;
        if (cachedData && cacheAgeMs < CACHE_TTL_MS) {
          try {
            const parsed = JSON.parse(cachedData);
            if (Array.isArray(parsed) && parsed.length > 0) {
              console.log('[HomePage] ✓ Using fresh cached data:', parsed.length, 'sections');
              setCmsData(parsed);
              if (cachedMedia) {
                const parsedMedia = JSON.parse(cachedMedia);
                if (parsedMedia && typeof parsedMedia === 'object') {
                  setMediaMap(parsedMedia);
                }
              }
              setIsLoading(false);
              hasFreshCache = true;
            }
          } catch (e) {
            console.warn('[HomePage] Could not parse cached data:', e.message);
          }
        }

        if (hasFreshCache) {
          return;
        }
        
        // STEP 2: Cache is stale or missing - fetch fresh sections AND media in PARALLEL
        console.log('[HomePage] Cache age:', cacheAgeMs, 'ms - fetching fresh sections & media in parallel');
        setIsLoading(true);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          console.warn('[HomePage] Fetch timeout (5s) - keeping old data');
          controller.abort();
        }, 5000); // 5 second timeout
        
        const cacheBuster = Date.now();
        const [sectionsResponse, mediaResponse] = await Promise.all([
          fetch(
            `${API_BASE}/homepage/cms/sections?published=true&_t=${cacheBuster}`,
            {
              credentials: 'omit',
              signal: controller.signal,
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
              }
            }
          ),
          fetch(
            `${API_BASE}/homepage/cms/media?type=image&_t=${cacheBuster}`,
            {
              credentials: 'omit',
              signal: controller.signal,
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache'
              }
            }
          ).catch(e => {
            console.warn('[HomePage] Media fetch error:', e);
            return null;
          })
        ]);
        
        clearTimeout(timeoutId);

        // Process media response
        let newMediaMap = {};
        if (mediaResponse && mediaResponse.ok) {
          try {
            const mediaResult = await mediaResponse.json();
            if (mediaResult.ok && Array.isArray(mediaResult.data)) {
              mediaResult.data.forEach(m => {
                if (m.url) {
                  let u = m.url;
                  if (u.includes('highlight_') && u.includes('/uploads/homepage/')) {
                    const filename = u.split('/').pop().split('?')[0];
                    const hostMatch = u.match(/^(https?:\/\/[^/]+)/i);
                    const origin = hostMatch ? hostMatch[1] : '';
                    u = `${origin}/api/uploads/images/${filename}`;
                  }
                  u = u.replace(/\/api\/api\//g, '/api/');
                  newMediaMap[m.id] = u;
                  newMediaMap[String(m.id)] = u;
                }
              });
              console.log('[HomePage] ✓ Populated mediaMap with', Object.keys(newMediaMap).length, 'entries');
              setMediaMap(newMediaMap);
              try {
                sessionStorage.setItem('cms_media_homepage', JSON.stringify(newMediaMap));
              } catch (e) {}
            }
          } catch (e) {
            console.warn('[HomePage] Error parsing media response:', e);
          }
        }
        
        // Process sections response
        if (sectionsResponse && sectionsResponse.ok) {
          const data = await sectionsResponse.json();
          if (data.ok && Array.isArray(data.data)) {
            console.log('[HomePage] ✓ Fresh data received:', data.data.length, 'sections');
            sessionStorage.setItem('cms_sections_homepage', JSON.stringify(data.data));
            sessionStorage.setItem('cms_cache_time', Date.now().toString());
            setCmsData(data.data);
          } else {
            throw new Error('Invalid API response structure');
          }
        } else {
          throw new Error('API error ' + (sectionsResponse ? sectionsResponse.status : 'no response'));
        }
      } catch (err) {
        console.error('[HomePage] Fetch error:', err.message);
        // If fetch fails but we have old cached data, keep using it
        const cachedData = sessionStorage.getItem('cms_sections_homepage');
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            if (Array.isArray(parsed) && parsed.length > 0) {
              console.log('[HomePage] Fetch failed, keeping old cached data');
              setCmsData(parsed);
            }
          } catch (e) {
            console.warn('[HomePage] Could not use fallback cache');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCmsData();
  }, []);
  
  // CRITICAL FIX: Add listener for approval completion messages
  // When principal approves changes, refetch BOTH cmsData AND mediaMap IN PARALLEL
  useEffect(() => {
    const handleApprovalComplete = async () => {
      console.log('[HomePage] 🎉 Approval completed event RECEIVED!');
      
      // CRITICAL: IMMEDIATELY set flag BEFORE fetching
      // This way, if user leaves tab and comes back, cache will be expired
      const currentTime = Date.now();
      sessionStorage.setItem('lastApprovalTime', currentTime.toString());
      sessionStorage.setItem('needsCmsRefresh', 'true');
      console.log('[HomePage] ✓ SessionStorage flags set at', new Date(currentTime).toISOString());
      
      try {
        // STEP 0: CLEAR ALL CACHES to force fresh load
        // CRITICAL: Clear ALL possible cache key variants
        sessionStorage.removeItem('homepage_cms_cache');
        sessionStorage.removeItem('homepage_media_cache');
        sessionStorage.removeItem('cms_sections_cache');
        localStorage.removeItem('cms_sections_homepage');  // CRITICAL: Match actual key name
        localStorage.removeItem('cms_sections_cache');      // Also clear old key if exists
        localStorage.removeItem('cms_cache_time');
        console.log('[HomePage] ✓ All caches cleared (homepage & cache variants)');
        
        // Step 1: Refetch BOTH CMS and Media data in PARALLEL
        // This is critical for performance - no sequential waits
        const cacheBuster = new Date().getTime();
        
        console.log('[HomePage] Starting PARALLEL refetch after approval...');
        const [cmsResponse, mediaResponse] = await Promise.all([
          // Fetch 1: Fresh CMS data
          fetch(`${API_BASE}/homepage/cms/sections?published=true&_t=${cacheBuster}`, {
            credentials: 'omit',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          }),
          // Fetch 2: Fresh Media (for image updates)
          fetch(`${API_BASE}/homepage/cms/media?folder=highlights&type=image&_t=${cacheBuster}`, {
            credentials: 'omit',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          })
        ]);
        
        console.log('[HomePage] ✓ Parallel refetch completed');
        console.log('[HomePage]   CMS response status:', cmsResponse.status);
        console.log('[HomePage]   Media response status:', mediaResponse.status);
        
        // Step 2: Parse CMS response
        if (!cmsResponse.ok) {
          throw new Error('Failed to fetch CMS data: ' + cmsResponse.status);
        }
        
        const cmsResult = await cmsResponse.json();
        
        if (!cmsResult.data || !Array.isArray(cmsResult.data)) {
          throw new Error('Invalid CMS data structure');
        }
        
        // Check highlights specifically
        const highlights = cmsResult.data.find(s => s.section_type === 'highlights');
        if (highlights) {
          const items = highlights.content_published?.en?.items || [];
          console.log('[HomePage] Highlights items after refetch:', items.length);
          if (items.length > 0) {
            console.log('[HomePage] First item media_id:', items[0].media_id);
          }
        }
        
        // Step 3: Parse Media response
        let mediaMap = {};
        if (mediaResponse.ok) {
          const mediaResult = await mediaResponse.json();
          if (mediaResult.ok && mediaResult.data && Array.isArray(mediaResult.data)) {
            mediaResult.data.forEach(media => {
              if (media.url) {
                mediaMap[media.id] = media.url;
              }
            });
            console.log('[HomePage] ✓ Updated media map with', Object.keys(mediaMap).length, 'images');
          }
        } else {
          console.warn('[HomePage] Media refetch had issues but continuing:', mediaResponse.status);
        }
        
        // Step 4: Update state with BOTH fresh data sources
        console.log('[HomePage] ✓ Setting CMS data state...');
        setCmsData(cmsResult.data);
        console.log('[HomePage] ✓ CMS data state updated');
        
        console.log('[HomePage] ✓ Setting media map state...');
        setMediaMap(mediaMap);
        console.log('[HomePage] ✓ Media map state updated');
        
        // Step 5: Cache fresh data for next page load
        try {
          localStorage.setItem('cms_sections_cache', JSON.stringify(cmsResult.data));
          localStorage.setItem('cms_cache_time', Date.now().toString());  // CRITICAL: Update cache time
          console.log('[HomePage] ✓ Cached fresh CMS data after approval');
        } catch (e) {
          console.warn('[HomePage] Could not cache after approval:', e);
        }
        
      } catch (error) {
        console.error('[HomePage] ❌ Error refetching after approval:', error.message);
      }
    };
    
    // Listen for custom event from approval modal
    console.log('[HomePage] ✓ Attaching approvalCompleted listener');
    window.addEventListener('approvalCompleted', handleApprovalComplete);
    
    return () => {
      console.log('[HomePage] ✓ Removing approvalCompleted listener');
      window.removeEventListener('approvalCompleted', handleApprovalComplete);
    };
  }, []);

  // FALLBACK REFETCH: If sessionStorage flag is set, refetch CMS data
  useEffect(() => {
    const doRefetchNow = sessionStorage.getItem('doRefetchNow');
    if (doRefetchNow === 'true' && cmsData) {
      console.log('[HomePage] ✓ Fallback refetch triggered after initial load');
      
      const refetchCMS = async () => {
        try {
          const cacheBuster = new Date().getTime();
          const url = `${API_BASE}/homepage/cms/sections?published=true&_t=${cacheBuster}`;
          const response = await fetch(url, {
            credentials: 'omit',
            headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' }
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.data) {
              console.log('[HomePage] ✓ Fallback refetch succeeded, updating state');
              setCmsData(result.data);
            }
          }
        } catch (e) {
          console.error('[HomePage] Fallback refetch error:', e);
        } finally {
          sessionStorage.removeItem('doRefetchNow');
          sessionStorage.removeItem('needsCmsRefresh');
        }
      };
      
      refetchCMS();
    }
  }, [cmsData]);
  
  // No longer needed - media is now fetched in parallel with CMS data
  // This eliminates the sequential loading issue that was causing "Loading..." messages
  
  // Get translations
  const copy = translations[activeLanguage];
  
  // CRITICAL DEBUG: Log if we're falling back (indicates API/cache issue)
  useEffect(() => {
    if (!cmsData || cmsData.length === 0) {
      console.warn('[HomePage] ⚠️ WARNING: Using fallback hardcoded data - API might be down or cache is empty');
    }
  }, [cmsData]);
  
  // CRITICAL: Check if we should render
  // Only show data if it's actually available from the database
  // Always render all sections - show whatever data exists from content_published
  const hasData = true;  // Always show sections (will display database data)
  
  // Countdown Section - INITIALIZE FIRST (before using countdownContent)
  const countdownSection = cmsData?.find(s => s.section_type === 'countdown');
  const countdownContent = (() => {
    if (!countdownSection) {
      return null;
    }

    // CRITICAL: ALWAYS prioritize content_published (approved/live data)
    const content = parseJsonContent(countdownSection.content_published || countdownSection.content);
    if (!content || typeof content !== 'object') {
      return null;
    }

    return content[activeLanguage] || content.en || content.id || content;
  })();

  const countdownDateFromDB = countdownContent?.countdown_date ||
    countdownContent?.target_date ||
    countdownContent?.date ||
    null;

  // Calculate countdown from database date only (no fallback)
  let countdownDateToUse;
  if (countdownDateFromDB) {
    const parsedCountdownDate = new Date(countdownDateFromDB).getTime();
    if (!Number.isNaN(parsedCountdownDate)) {
      countdownDateToUse = parsedCountdownDate;
    }
  }
  // If no database date, countdownDateToUse remains undefined (no countdown display)
  
  // ============================================================
  // EXTRACT DATA FROM CMS PUBLISHED (Primary source)
  // Fallback to homepageContent.js only if CMS data unavailable
  // ============================================================
  
  // Hero Section
  const heroSection = cmsData?.find(s => s.section_type === 'hero');
  
  // CRITICAL FIX: ALWAYS prioritize content_published (approved data)
  // Frontend MUST read published data first, fallback to draft only if no published exists
  let heroContent = null;
  if (heroSection) {
    // Try published first (this is live data that was approved)
    const published = parseJsonContent(heroSection.content_published);
    if (published) {
      heroContent = published[activeLanguage] || published['en'] || published['id'];
    }
    
    // Only fallback to draft if no published version exists
    if (!heroContent) {
      const draft = parseJsonContent(heroSection.content);
      if (draft) {
        heroContent = draft[activeLanguage] || draft['en'] || draft['id'];
        console.warn('[HomePage] Using DRAFT hero content (no published version available)');
      }
    }
  }
  
  console.log('[HomePage.js]   heroContent:', heroContent);
  console.log('[HomePage.js]   heroContent.video_url:', heroContent?.video_url);
  
  // DEBUG: Log at each step
  if (typeof window !== 'undefined' && window.__DEBUG_HERO) {
    console.log('=== HERO DEBUG ===');
    console.log('activeLanguage:', activeLanguage);
    console.log('Step 1 - heroSection:', JSON.stringify(heroSection, null, 2));
    console.log('Step 2 - heroContent (from language key):', JSON.stringify(heroContent, null, 2));
  }
  
  // Extract hero fields - NO hardcoded fallback, only database data
  const extractField = (obj, keys) => {
    if (!obj) return null;  // Return null if no data (not empty string)
    for (const key of keys) {
      const value = obj[key];
      if (value && typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }
    return null;  // Return null if not found (not empty string)
  };

  // CRITICAL: Use ONLY database data from content_management table
  // NO hardcoded fallback - only show data from database
  // If API hasn't responded yet, fields will be empty until data arrives
  const heroData = {
    taglinePrefix: extractField(heroContent, ['tagline_prefix', 'taglinePrefix']),
    taglineAccent: extractField(heroContent, ['tagline_accent', 'taglineAccent']),
    philosophy: extractField(heroContent, ['philosophy']),
    // CRITICAL: Check both video_file AND video_url (database uses video_file)
    video_file: extractField(heroContent, ['video_file', 'videoFile', 'video_url', 'videoUrl']),
    video_url: extractField(heroContent, ['video_url', 'videoUrl', 'video_file', 'videoFile'])
  };
  
  console.log('[HomePage.js] HERO DATA PREPARED');
  console.log('[HomePage.js]   heroContent:', heroContent);
  console.log('[HomePage.js]   heroContent.video_url:', heroContent?.video_url);
  console.log('[HomePage.js]   heroData:', heroData);
  console.log('[HomePage.js]   heroData.video_url:', heroData.video_url);
  console.log('[HomePage.js]   About to pass to HeroSection');
  
  // CRITICAL DIAGNOSTIC: Log what we're actually sending to HeroSection
  if (typeof window !== 'undefined') {
    window.__HERO_DATA_LOG = {
      timestamp: new Date().toISOString(),
      taglineAccent: heroData.taglineAccent,
      philosophy: heroData.philosophy,
      video_url: heroData.video_url
    };
    console.log('[HomePage.js] ⚠️ HERO DATA DIAGNOSTIC:', window.__HERO_DATA_LOG);
  }
  
  if (typeof window !== 'undefined' && window.__DEBUG_HERO) {
    console.log('Step 3 - heroData (to HeroSection):', JSON.stringify(heroData, null, 2));
    console.log('  video_url:', heroData.video_url);
    console.log('  video_url type:', typeof heroData.video_url);
  }
  
  // AUDIT: Log heroData before passing to HeroSection
  if (typeof window !== 'undefined') {
    const auditData = {
      timestamp: new Date().toISOString(),
      source: 'HomePage.js',
      heroData: heroData,
      cmsDataAvailable: !!cmsData,
      heroSectionFound: !!heroSection,
      heroContent: heroContent
    };
    console.log('[HomePage.js] heroData before HeroSection:', heroData);
    console.log('[HomePage.js] cmsData available:', !!cmsData);
    console.log('[HomePage.js] heroSection from cmsData:', heroSection);
    
    // Backend logging disabled in production
    // (was causing CORS errors from https domain to localhost)
  }
  
  // Quick Links Section - Use PUBLISHED content (approved changes)
  const quickLinksSection = cmsData?.find(s => s.section_type === 'quicklinks');
  
  // CRITICAL FIX: Prioritize content_published (approved data)
  // This ensures always showing live approved content, not draft
  let quickLinksContent = null;
  if (quickLinksSection) {
    const published = parseJsonContent(quickLinksSection.content_published);
    if (published) {
      quickLinksContent = published['en'] || published;
    }
    
    if (!quickLinksContent) {
      const draft = parseJsonContent(quickLinksSection.content);
      if (draft) {
        quickLinksContent = draft['en'] || draft;
        console.warn('[HomePage] Using DRAFT quicklinks (no published version)');
      }
    }
  }
  
  const quickLinksData = quickLinksContent ? (() => {
    // Support both legacy structure (individual objects) and new structure (cards array)
    let applyNow, schoolCalendar, contactUs;
    
// Try to get from cards array first (new structure)
    if (quickLinksContent.cards && Array.isArray(quickLinksContent.cards)) {
      const applyCard = quickLinksContent.cards.find(c => c.id === 'apply') || quickLinksContent.cards[0];
      const calendarCard = quickLinksContent.cards.find(c => c.id === 'calendar') || quickLinksContent.cards[1];
      const contactCard = quickLinksContent.cards.find(c => c.id === 'contact') || quickLinksContent.cards[2];
      
      applyNow = applyCard ? { ...applyCard, link: applyCard.link || 'register' } : null;
      schoolCalendar = calendarCard ? { ...calendarCard, link: calendarCard.link || '#school-calendar' } : null;
      contactUs = contactCard ? { ...contactCard, link: contactCard.link || 'contact-us#contact-form' } : null;
    }
    
    // Fall back to legacy structure if not found in cards
    applyNow = applyNow || quickLinksContent.apply_now || quickLinksContent.applyNow;
    schoolCalendar = schoolCalendar || quickLinksContent.school_calendar || quickLinksContent.schoolCalendar;
    contactUs = contactUs || quickLinksContent.contact_us || quickLinksContent.contactUs;
    
    const formattedCards = quickLinksContent.cards && Array.isArray(quickLinksContent.cards)
      ? quickLinksContent.cards.map((c, i) => ({
          ...c,
          link: c.link || (c.id === 'apply' || i === 0 ? 'register' : c.id === 'contact' || i === 2 ? 'contact-us#contact-form' : '#school-calendar')
        }))
      : (applyNow && schoolCalendar && contactUs ? [
          { id: 'apply', ...(applyNow || {}), link: applyNow?.link || 'register' },
          { id: 'calendar', ...(schoolCalendar || {}), link: schoolCalendar?.link || '#school-calendar' },
          { id: 'contact', ...(contactUs || {}), link: contactUs?.link || 'contact-us#contact-form' }
        ] : null);
    
    return {
      kicker: quickLinksContent.kicker || '',
      title: quickLinksContent.title || '',
      cards: formattedCards,
      applyNow: applyNow || null,
      schoolCalendar: schoolCalendar || null,
      contactUs: contactUs || null
    };
  })() : {
    // No quicklinks data in database - return empty object
    kicker: '',
    title: '',
    applyNow: null,
    schoolCalendar: null,
    contactUs: null
  };
  
  // Highlights Section - Use PUBLISHED content (approved changes)
  const highlightsSection = cmsData?.find(s => s.section_type === 'highlights');
  
  // CRITICAL DEBUG: Log raw section before parsing
  useEffect(() => {
    if (cmsData && Array.isArray(cmsData)) {
      const h = cmsData.find(s => s.section_type === 'highlights');
      if (h) {
        console.log('[HomePage] HIGHLIGHTS SECTION RAW DATA:');
        console.log('[HomePage]   typeof content_published:', typeof h.content_published);
        console.log('[HomePage]   content_published first 200 chars:', 
          (typeof h.content_published === 'string' 
            ? h.content_published.substring(0, 200) 
            : JSON.stringify(h.content_published).substring(0, 200)));
      }
    }
  }, [cmsData]);
  
  // CRITICAL FIX: Prioritize content_published (approved data)
  let highlightsContent = null;
  if (highlightsSection) {
    const published = parseJsonContent(highlightsSection.content_published);
    console.log('[HomePage] After parseJsonContent for highlights:');
    console.log('[HomePage]   parsed type:', typeof published);
    console.log('[HomePage]   parsed has en?:', !!published?.en);
    console.log('[HomePage]   parsed keys:', Object.keys(published || {}));
    
    if (published) {
      const enItems = published['en']?.items || [];
      const langContent = published[activeLanguage] || published['en'] || published;
      if (langContent && Array.isArray(langContent.items)) {
        langContent.items = langContent.items.map((it, idx) => {
          const enFallback = enItems[idx] || {};
          return {
            ...it,
            media_id: it.media_id || enFallback.media_id || null,
            image: it.image || enFallback.image || null
          };
        });
      }
      highlightsContent = langContent;
      console.log('[HomePage]   ✓ highlightsContent set from published');
      console.log('[HomePage]     has items?:', !!highlightsContent?.items);
      console.log('[HomePage]     items length:', highlightsContent?.items?.length);
    }
    
    if (!highlightsContent) {
      const draft = parseJsonContent(highlightsSection.content);
      if (draft) {
        const enItems = draft['en']?.items || [];
        const langDraft = draft[activeLanguage] || draft['en'] || draft;
        if (langDraft && Array.isArray(langDraft.items)) {
          langDraft.items = langDraft.items.map((it, idx) => {
            const enFallback = enItems[idx] || {};
            return {
              ...it,
              media_id: it.media_id || enFallback.media_id || null,
              image: it.image || enFallback.image || null
            };
          });
        }
        highlightsContent = langDraft;
        console.warn('[HomePage] Using DRAFT highlights (no published version)');
      }
    }
  }
  
  // CRITICAL DEBUG: Log what we got from API
  useEffect(() => {
    if (cmsData && Array.isArray(cmsData)) {
      const h = cmsData.find(s => s.section_type === 'highlights');
      if (h) {
        const cp = h.content_published;
        const items = cp?.en?.items || [];
        console.log('[HomePage] ✓ HIGHLIGHTS RECEIVED FROM API');
        console.log('[HomePage]   content_published exists:', !!cp);
        console.log('[HomePage]   content_published.en exists:', !!cp?.en);
        console.log('[HomePage]   items array length:', items.length);
        if (items.length > 0) {
          console.log('[HomePage]   First item:', items[0]);
        }
      }
    }
  }, [cmsData]);
  
  // Extract items - CRITICAL FIX: Only use content_published items
  let highlightItems = [];
  
  console.log('[HomePage] EXTRACTING HIGHLIGHT ITEMS:');
  console.log('[HomePage]   highlightsContent exists:', !!highlightsContent);
  console.log('[HomePage]   highlightsContent?.items exists:', !!highlightsContent?.items);
  console.log('[HomePage]   highlightsContent?.items is array:', Array.isArray(highlightsContent?.items));
  console.log('[HomePage]   highlightsContent?.items length:', highlightsContent?.items?.length);
  
  // Try to get from published content first
  if (highlightsContent && highlightsContent.items && Array.isArray(highlightsContent.items) && highlightsContent.items.length > 0) {
    console.log('[HomePage] ✓ Found', highlightsContent.items.length, 'highlight items from API');
    highlightItems = highlightsContent.items;
  } else if (highlightsSection && highlightsSection.items && Array.isArray(highlightsSection.items) && highlightsSection.items.length > 0) {
    console.log('[HomePage] ✓ Using items from section.items (from homepage_items table)');
    highlightItems = highlightsSection.items;
  } else {
    console.warn('[HomePage] ⚠️ No highlight items found in any source');
  }
  
  // Map and normalize items
  if (highlightItems && Array.isArray(highlightItems) && highlightItems.length > 0) {
    console.log('[HomePage] Mapping', highlightItems.length, 'items');
    highlightItems = highlightItems.map((item, idx) => {
      if (typeof item === 'object' && item !== null) {
        const mapped = {
          ...item,
          title: item.title || '',
          summary: item.summary || '',
          media_id: item.media_id || null,
          image: item.image || item.photo || null
        };
        console.log(`[HomePage] Item[${idx}]:`, mapped.title.substring(0, 30), 'media_id=' + mapped.media_id, 'image=' + mapped.image);
        return mapped;
      }
      return item;
    });
  }
  
  // Show highlights only if database has items - no fallback
  if (!highlightItems || highlightItems.length === 0) {
    console.log('[HomePage] No highlights items from database');
    highlightItems = [];  // Empty array (sections will show empty state from component)
  }
  
  console.log('[HomePage] FINAL: highlightItems.length =', highlightItems.length);
  if (highlightItems.length > 0) {
    console.log('[HomePage] First item:', highlightItems[0].title);
  }
  
  // CRITICAL FIX: ALWAYS create highlightsData object with items from database only
  // Show what's in database - empty array if no items
  const highlightsData = {
    kicker: highlightsContent?.kicker || '',
    title: highlightsContent?.title || '',
    intro: highlightsContent?.intro || '',
    liveLabel: highlightsContent?.live_label || highlightsContent?.liveLabel || '',
    liveNote: highlightsContent?.live_note || highlightsContent?.liveNote || '',
    // ONLY: Use database items, no fallback
    items: (highlightItems && highlightItems.length > 0) ? highlightItems : []
  };
  
  console.log('[HomePage] HIGHLIGHTS DATA FINAL:');
  console.log('[HomePage]   kicker:', highlightsData?.kicker);
  console.log('[HomePage]   title:', highlightsData?.title);
  console.log('[HomePage]   items.length:', highlightsData?.items?.length);
  
  // News Section - CRITICAL FIX: Always prioritize published content
  const newsSection = cmsData?.find(s => s.section_type === 'news');
  
  let newsContent = null;
  if (newsSection) {
    // CRITICAL: Prioritize content_published (approved data)
    const published = parseJsonContent(newsSection.content_published);
    if (published) {
      const enItems = published['en']?.items || [];
      const langContent = published[activeLanguage] || published['en'] || published;
      if (langContent && Array.isArray(langContent.items)) {
        langContent.items = langContent.items.map((it, idx) => {
          const enFallback = enItems[idx] || {};
          return {
            ...it,
            media_id: it.media_id || enFallback.media_id || null,
            image: it.image || enFallback.image || null
          };
        });
      }
      newsContent = langContent;
      console.log('[HomePage] Using PUBLISHED news content:', newsContent?.items?.length || 0, 'items');
    }
    
    // Fallback to draft only if no published version
    if (!newsContent && newsSection.content) {
      try {
        const content = typeof newsSection.content === 'string' 
          ? JSON.parse(newsSection.content)
          : newsSection.content;
        const enItems = content?.en?.items || [];
        const langDraft = content?.[activeLanguage] || content?.en || content;
        if (langDraft && Array.isArray(langDraft.items)) {
          langDraft.items = langDraft.items.map((it, idx) => {
            const enFallback = enItems[idx] || {};
            return {
              ...it,
              media_id: it.media_id || enFallback.media_id || null,
              image: it.image || enFallback.image || null
            };
          });
        }
        newsContent = langDraft;
        console.warn('[HomePage] Using DRAFT news content (no published version):', newsContent?.items?.length || 0, 'items');
      } catch (e) {
        console.error('[HomePage] Error parsing news content:', e);
      }
    }
    
    // If still no content but section.items exists (from homepage_items table), use that
    if (!newsContent && newsSection.items && Array.isArray(newsSection.items) && newsSection.items.length > 0) {
      console.log('[HomePage] Using news items from homepage_items table:', newsSection.items.length, 'items');
      newsContent = {
        kicker: newsSection.kicker || copy.newsCountdownKicker,
        title: newsSection.title || copy.newsCountdownTitle,
        intro: newsSection.intro || copy.newsCountdownIntro,
        items: newsSection.items
      };
    }
  }

  // Automatic Homepage News Integration: Pull the 3 latest approved articles from Blog & Articles
  const newsArticlesSection = cmsData?.find(s => 
    s.section_type === 'news-articles' || 
    s.section_type === 'news-blog-articles' || 
    s.section_type === 'news-list' || 
    s.section_type === 'articles'
  );

  useEffect(() => {
    if (!newsArticlesSection && !externalNewsArticles) {
      const fetchNewsArticles = async () => {
        try {
          const res = await fetch(`${API_BASE}/homepage/cms/sections?published=true&page=news-updates&_t=${Date.now()}`, {
            credentials: 'omit'
          });
          if (res.ok) {
            const json = await res.json();
            const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
            const found = items.find(s => 
              s.section_type === 'news-articles' || 
              s.section_type === 'news-blog-articles' || 
              s.section_type === 'news-list' || 
              s.section_type === 'articles'
            );
            if (found) {
              const pub = parseJsonContent(found.content_published) || parseJsonContent(found.content);
              const en = pub?.en || pub || {};
              const list = Array.isArray(en.items) && en.items.length > 0 
                ? en.items 
                : (Array.isArray(en.blog_articles) ? en.blog_articles : []);
              if (list.length > 0) {
                setExternalNewsArticles(list);
              }
            }
          }
        } catch (e) {
          console.warn('[HomePage] Could not fetch external news articles:', e);
        }
      };
      fetchNewsArticles();
    }
  }, [newsArticlesSection, externalNewsArticles]);

  const rawArticlesList = (() => {
    if (newsArticlesSection) {
      const pub = parseJsonContent(newsArticlesSection.content_published) || parseJsonContent(newsArticlesSection.content);
      const en = pub?.[activeLanguage] || pub?.en || pub || {};
      const list = Array.isArray(en.items) && en.items.length > 0 
        ? en.items 
        : (Array.isArray(en.blog_articles) ? en.blog_articles : []);
      if (list.length > 0) return list;
    }
    if (externalNewsArticles && externalNewsArticles.length > 0) {
      return externalNewsArticles;
    }
    return null;
  })();

  // Extract up to 3 latest articles from the articles system
  const dynamicHomepageNewsItems = (rawArticlesList && rawArticlesList.length > 0)
    ? rawArticlesList.slice(0, 3).map((item, idx) => ({
        id: item.id || `article-${idx + 1}`,
        date: formatNewsDate(item.date, activeLanguage) || item.date || '',
        title: item.title || '',
        summary: item.description || item.summary || '',
        category: item.category || item.tag || '',
        media_id: item.media_id || null,
        image: resolveMediaUrl(item.media_id, item.image, mediaMap) || item.image,
        link: item.link || (item.id ? `news-blog-${item.id}` : '')
      }))
    : ((newsContent?.items && newsContent.items.length > 0)
        ? newsContent.items.map((item, idx) => ({
            id: item.id || `news-${idx + 1}`,
            date: formatNewsDate(item.date, activeLanguage) || item.date || '',
            title: item.title || '',
            summary: item.summary || item.description || '',
            category: item.category || '',
            media_id: item.media_id || null,
            image: resolveMediaUrl(item.media_id, item.image, mediaMap) || item.image,
            link: item.link || (item.id ? `news-blog-${item.id}` : '')
          }))
        : []);

  // Merge news and countdown data for component
  const countdownLabel = countdownContent?.label || countdownContent?.countdown_label || copy.countdownLabel;
  const countdownTitle = countdownContent?.title || countdownContent?.countdown_title || copy.countdownTitle;
  const countdownDate = formatCountdownDate(countdownDateFromDB, activeLanguage) ||
    countdownContent?.date_text ||
    countdownContent?.countdown_date_text ||
    countdownContent?.dateText ||
    countdownContent?.countdownDateText ||
    copy.countdownDateText;
  const countdownDescription = countdownContent?.description || countdownContent?.countdown_description || copy.countdownDescription;
  const countdownDaysLabel = countdownContent?.days_label || countdownContent?.daysLabel || copy.countdownDays;
  const countdownHoursLabel = countdownContent?.hours_label || countdownContent?.hoursLabel || copy.countdownHours;
  const countdownMinutesLabel = countdownContent?.minutes_label || countdownContent?.minutesLabel || copy.countdownMinutes;
  const countdownSecondsLabel = countdownContent?.seconds_label || countdownContent?.secondsLabel || copy.countdownSeconds;
  const countdownComplete = countdownContent?.complete_message || countdownContent?.completeMessage || copy.countdownComplete;
  
  const newsData = {
    kicker: newsContent?.kicker || copy.newsCountdownKicker || 'News & Events',
    title: newsContent?.title || copy.newsCountdownTitle || 'Latest News',
    intro: newsContent?.intro || copy.newsCountdownIntro || 'Stay updated with our latest news',
    latestNewsLabel: newsContent?.latest_news_label || newsContent?.latestNewsLabel || copy.latestNewsLabel || 'Latest News',
    newsItems: dynamicHomepageNewsItems,
    countdownLabel,
    countdownTitle,
    countdownDate,
    countdownDescription,
    countdownDays: countdownDaysLabel,
    countdownHours: countdownHoursLabel,
    countdownMinutes: countdownMinutesLabel,
    countdownSeconds: countdownSecondsLabel,
    countdownComplete,
    isLoading: !hasData
  };
  
  // DEBUG: Log newsData structure
  useEffect(() => {
    console.log('[HomePage] newsData.newsItems count:', newsData?.newsItems?.length || 0);
    if (newsData?.newsItems?.length > 0) {
      console.log('[HomePage] First newsItem:', newsData.newsItems[0]);
      console.log('[HomePage] newsItems titles:', newsData.newsItems.map(i => i.title).join(', '));
    }
  }, [newsData]);

  useEffect(() => {
    if (!onLanguageChange && language !== localLanguage) {
      setLocalLanguage(language);
    }
  }, [language, onLanguageChange, localLanguage]);

  // Track whether we've initialized the random index (only do it ONCE on mount)
  const [hasInitializedIndex, setHasInitializedIndex] = useState(false);

  useEffect(() => {
    // CRITICAL FIX: Only set random highlight index ONCE when ALL data is ready
    // This prevents resetting on language change or other updates
    
    // Guard 1: Check if we've already initialized
    if (hasInitializedIndex) {
      return; // Already initialized, don't run again
    }
    
    // Guard 2: Wait for highlight items
    if (!highlightItems || highlightItems.length === 0) {
      console.log('[HomePage] Waiting for highlight items...');
      return;
    }
    
    // Initialize random index and start auto-rotation
    console.log('[HomePage] ✓ Setting random highlight index with', highlightItems.length, 'items');
    
    const randomIndex = Math.floor(Math.random() * highlightItems.length);
    setHighlightIndex(randomIndex);
    setHasInitializedIndex(true);

    // Set up auto-rotation interval
    const intervalId = setInterval(() => {
      setHighlightIndex((currentIndex) => {
        return getNextHighlightIndex(currentIndex, highlightItems.length);
      });
    }, HIGHLIGHT_ROTATION_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [highlightItems, hasInitializedIndex]);

  useEffect(() => {
    const tickerId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickerId);
  }, []);

  const countdownData = calculateCountdown(countdownDateToUse, now);
  const countdownDays = countdownData.days;
  const countdownHours = countdownData.hours;
  const countdownMinutes = countdownData.minutes;
  const countdownSeconds = countdownData.seconds;
  const isOpeningDayReached = countdownData.isReached;



  if (!cmsData || cmsData.length === 0) {
    return (
      <div className="homepage-loading-screen">
        <div className="loading-spinner"></div>
        <p>{activeLanguage === 'id' ? 'Memuat Permata Harapan School...' : 'Loading Permata Harapan School...'}</p>
      </div>
    );
  }

  return (
    <div className="homepage">
      {cmsData && cmsData.length > 0 && (
        <>
          {/* Language Switcher */}
          <div className="hero-language-switcher" aria-label="Language switcher">
            <span className="hero-language-label">
              {activeLanguage === 'en' ? 'Language' : 'Bahasa'}
            </span>
            <div className="hero-language-options" role="group" aria-label="Select page language">
              <button
                type="button"
                className={`hero-language-button ${activeLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setLanguage('en')}
              >
                EN
              </button>
              <button
                type="button"
                className={`hero-language-button ${activeLanguage === 'id' ? 'active' : ''}`}
                onClick={() => setLanguage('id')}
              >
                ID
              </button>
            </div>
          </div>

          {/* Hero Component */}
          <HeroSection data={heroData} />

          {/* CMS Edit Overlay */}
          {editableMode && selectedSection === 'hero' && onEditSection && (
            <div className="cms-section-overlay" onClick={(e) => e.stopPropagation()}>
              <div className="cms-section-overlay-badge">Hero Section</div>
              <div className="cms-section-overlay-buttons">
                <button 
                  className="cms-overlay-btn cms-overlay-btn-edit"
                  onClick={() => onEditSection('hero')}
                >
                  <Edit size={16} style={{marginRight: '4px', display: 'inline-block', verticalAlign: 'middle'}} /> Edit Section
                </button>
                <button 
                  className="cms-overlay-btn cms-overlay-btn-preview"
                  title="Preview draft changes"
                >
                  <Eye size={16} style={{marginRight: '4px', display: 'inline-block', verticalAlign: 'middle'}} /> Preview Draft
                </button>
              </div>
            </div>
          )}

          {/* Quick Links + Highlights + News */}
          {hasData && (
            <>
              <QuickLinksSection 
                data={quickLinksData}
                onNavigate={onNavigate}
                editableMode={editableMode}
                selectedSection={selectedSection}
                onEditSection={onEditSection}
              />

              {/* Highlights Section */}
              {(() => {
                const itemsToUse = highlightsData?.items || [];
                
                const images = itemsToUse.map((item, idx) => {
                  return resolveMediaUrl(item.media_id, item.image || item.photo || item.url, mediaMap);
                });
                
                return (
                  <HighlightsSection
                    data={highlightsData}
                    highlightIndex={highlightIndex}
                    onHighlightChange={setHighlightIndex}
                    images={images}
                    editableMode={editableMode}
                    selectedSection={selectedSection}
                    onEditSection={onEditSection}
                    onOpenItemManager={onOpenItemManager}
                  />
                );
              })()}

              {/* News & Countdown Section */}
              <NewsCountdownSection
                data={newsData}
                onNavigate={onNavigate}
                countdown={{
                  days: countdownDays,
                  hours: countdownHours,
                  minutes: countdownMinutes,
                  seconds: countdownSeconds,
                  isReached: isOpeningDayReached,
                }}
                images={(() => {
                  if (newsData?.newsItems && newsData.newsItems.length > 0) {
                    return newsData.newsItems.map((item, idx) => {
                      return resolveMediaUrl(item.media_id, item.image || item.photo || item.url, mediaMap);
                    });
                  }
                  return [];
                })()}
                currentLanguage={activeLanguage}
                editableMode={editableMode}
                selectedSection={selectedSection}
                onEditSection={onEditSection}
                onOpenItemManager={onOpenItemManager}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default HomePage;
